import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import {
  createListing,
  listMyListings,
  listExchange,
  updateListingStatus,
  refreshListingMatches,
  updateMatchStatus,
  LISTING_TYPES,
  type ListingType,
} from "@/lib/exchange.functions";
import {
  ArrowLeftRight,
  Banknote,
  Briefcase,
  Building2,
  Globe2,
  Handshake,
  KeyRound,
  Landmark,
  Package,
  Plus,
  RefreshCw,
  Search,
  FileSearch,
  ShoppingCart,
  Target,
  TrendingUp,
  FileSignature,
  X,
} from "lucide-react";
import { ContractFromMatch } from "@/components/contracts/ContractFromMatch";
import { toast } from "sonner";
import { buildSeo } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { safeStorageFileName } from "@/lib/document-extract";
import { Paperclip } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";

export const Route = createFileRoute("/_authenticated/exchange")({
  head: () =>
    buildSeo({
      path: "/exchange",
      title: "Business Exchange — Reality Twin",
      description:
        "Post concrete offers and needs — sell products, source suppliers, raise or deploy capital — and let the network match you with the right counterpart.",
      noindex: true,
    }),
  component: Page,
});

type FieldDef = { key: string; label: string; placeholder: string };

const TYPE_META: Record<
  ListingType,
  {
    icon: any;
    label: string;
    tagline: string;
    amountLabel: string;
    hue: string;
    fields: FieldDef[];
  }
> = {
  product_offer: {
    icon: Package,
    label: "Sell a product",
    tagline: "Find retailers, distributors and buyers for your product.",
    amountLabel: "Unit price range",
    hue: "168",
    fields: [
      { key: "category", label: "Product category", placeholder: "e.g. Organic skincare" },
      { key: "moq", label: "Minimum order", placeholder: "e.g. 100 units" },
      { key: "margin", label: "Reseller margin", placeholder: "e.g. 40%" },
      { key: "certifications", label: "Certifications", placeholder: "e.g. CE, BIO, ISO 9001" },
    ],
  },
  product_demand: {
    icon: ShoppingCart,
    label: "Source products",
    tagline: "You run a store or need products for your segment.",
    amountLabel: "Purchase budget",
    hue: "60",
    fields: [
      { key: "category", label: "Category wanted", placeholder: "e.g. Sustainable home goods" },
      { key: "volume", label: "Purchase volume", placeholder: "e.g. 500 units / month" },
      { key: "segment", label: "Customer segment", placeholder: "e.g. Urban 25-40, premium" },
    ],
  },
  invest_ask: {
    icon: TrendingUp,
    label: "Raise capital",
    tagline: "Present your venture with real numbers to matching investors.",
    amountLabel: "Funding ask",
    hue: "290",
    fields: [
      { key: "stage", label: "Stage", placeholder: "e.g. Seed" },
      { key: "revenue", label: "Annual revenue", placeholder: "e.g. 250k USD ARR" },
      { key: "growth", label: "Growth (YoY)", placeholder: "e.g. 180%" },
      { key: "valuation", label: "Valuation", placeholder: "e.g. 4M USD" },
      { key: "use_of_funds", label: "Use of funds", placeholder: "e.g. Sales team + product" },
    ],
  },
  invest_offer: {
    icon: Landmark,
    label: "Invest capital",
    tagline: "Define your ticket, stages and sectors — deals find you.",
    amountLabel: "Ticket size",
    hue: "240",
    fields: [
      { key: "stages", label: "Preferred stages", placeholder: "e.g. Pre-Seed to Series A" },
      { key: "sectors", label: "Sectors", placeholder: "e.g. SaaS, Fintech, Climate" },
      { key: "geography", label: "Geography", placeholder: "e.g. DACH, EU" },
    ],
  },
  service_offer: {
    icon: Briefcase,
    label: "Offer a service",
    tagline: "Put your capacity in front of companies that need it.",
    amountLabel: "Typical project size",
    hue: "20",
    fields: [
      { key: "category", label: "Service category", placeholder: "e.g. Performance marketing" },
      { key: "capacity", label: "Availability", placeholder: "e.g. 2 projects from August" },
    ],
  },
  service_demand: {
    icon: Search,
    label: "Find a supplier or service",
    tagline: "Describe what you need — matching providers are notified.",
    amountLabel: "Budget",
    hue: "330",
    fields: [
      {
        key: "category",
        label: "What you need",
        placeholder: "e.g. Logistics partner for e-commerce",
      },
      { key: "timeline", label: "Timeline", placeholder: "e.g. Start within 4 weeks" },
    ],
  },
  partnership: {
    icon: Handshake,
    label: "Strategic partnership",
    tagline: "Co-marketing, integrations, alliances — find companies that complement you.",
    amountLabel: "Deal size (optional)",
    hue: "200",
    fields: [
      {
        key: "focus",
        label: "Partnership focus",
        placeholder: "e.g. Co-marketing, tech integration",
      },
      {
        key: "bring",
        label: "What you bring",
        placeholder: "e.g. 40k newsletter reach, API platform",
      },
      {
        key: "seek",
        label: "What you look for",
        placeholder: "e.g. Complementary SaaS with SMB base",
      },
    ],
  },
  license_offer: {
    icon: KeyRound,
    label: "License out IP",
    tagline: "Monetize patents, brands, software or content through licensing.",
    amountLabel: "License fee range",
    hue: "140",
    fields: [
      {
        key: "asset",
        label: "Asset type",
        placeholder: "e.g. Patent, brand, white-label software",
      },
      { key: "territory", label: "Territory", placeholder: "e.g. DACH exclusive, worldwide" },
      { key: "model", label: "Royalty model", placeholder: "e.g. 5% rev-share, flat fee" },
    ],
  },
  license_demand: {
    icon: FileSearch,
    label: "Find technology / IP",
    tagline: "Looking for white-label tech, patents or brands to license in.",
    amountLabel: "Budget",
    hue: "100",
    fields: [
      { key: "seeking", label: "What you need", placeholder: "e.g. White-label booking software" },
      { key: "territory", label: "Territory", placeholder: "e.g. EU rights" },
    ],
  },
  business_sale: {
    icon: Building2,
    label: "Sell a business (M&A)",
    tagline: "Present your company to matching acquirers — with real numbers.",
    amountLabel: "Asking price",
    hue: "350",
    fields: [
      { key: "revenue", label: "Annual revenue", placeholder: "e.g. 1.2M CHF" },
      { key: "ebitda", label: "EBITDA", placeholder: "e.g. 300k CHF" },
      { key: "employees", label: "Employees", placeholder: "e.g. 8" },
      { key: "reason", label: "Reason for sale", placeholder: "e.g. Succession, new venture" },
    ],
  },
  business_buy: {
    icon: Target,
    label: "Acquire a business",
    tagline: "Define your target profile — sellers find you before brokers do.",
    amountLabel: "Acquisition budget",
    hue: "310",
    fields: [
      { key: "target", label: "Target profile", placeholder: "e.g. SaaS, 0.5-5M ARR, profitable" },
      { key: "industries", label: "Industries", placeholder: "e.g. Software, e-commerce" },
      { key: "geography", label: "Geography", placeholder: "e.g. DACH, remote-first" },
    ],
  },
};

function fmtAmount(
  min: number | null,
  max: number | null,
  currency: string,
  labels?: { from: string; upTo: string },
): string | null {
  const f = (n: number) =>
    n >= 1_000_000
      ? `${+(n / 1_000_000).toFixed(1)}M`
      : n >= 1000
        ? `${+(n / 1000).toFixed(0)}k`
        : String(n);
  const from = labels?.from ?? "from";
  const upTo = labels?.upTo ?? "up to";
  if (min != null && max != null) return `${f(min)}–${f(max)} ${currency}`;
  if (min != null) return `${from} ${f(min)} ${currency}`;
  if (max != null) return `${upTo} ${f(max)} ${currency}`;
  return null;
}

function TypeBadge({ type }: { type: string }) {
  const t = useHydratedTranslation();
  const meta = TYPE_META[type as ListingType];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md glass"
      style={{ color: `oklch(0.82 0.15 ${meta.hue})` }}
    >
      <Icon className="h-3 w-3" />{" "}
      {t(`app.pages.exchange.types.${type}.label`, {
        defaultValue: meta.label,
      })}
    </span>
  );
}

function DetailChips({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details ?? {}).filter(([, v]) => v !== "" && v != null);
  if (entries.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {entries.slice(0, 6).map(([k, v]) => (
        <span
          key={k}
          className="text-[11px] px-2 py-1 rounded-md bg-white/5 border border-border text-muted-foreground"
        >
          <span className="opacity-70">{k.replace(/_/g, " ")}:</span>{" "}
          <span className="text-foreground/90">{String(v)}</span>
        </span>
      ))}
    </div>
  );
}

function Page() {
  const t = useHydratedTranslation();
  const create = useServerFn(createListing);
  const fetchMine = useServerFn(listMyListings);
  const fetchBrowse = useServerFn(listExchange);
  const setListingStatus = useServerFn(updateListingStatus);
  const refreshMatches = useServerFn(refreshListingMatches);
  const setMatchStatus = useServerFn(updateMatchStatus);

  // Translated per-type meta (label/tagline/amountLabel), falling back to the
  // English strings baked into TYPE_META when a key is missing.
  const tmLabel = (type: ListingType) =>
    t(`app.pages.exchange.types.${type}.label`, { defaultValue: TYPE_META[type].label });
  const tmTagline = (type: ListingType) =>
    t(`app.pages.exchange.types.${type}.tagline`, { defaultValue: TYPE_META[type].tagline });
  const tmAmount = (type: ListingType) =>
    t(`app.pages.exchange.types.${type}.amountLabel`, {
      defaultValue: TYPE_META[type].amountLabel,
    });
  const amtLabels = {
    from: t("app.pages.exchange.fromAmt"),
    upTo: t("app.pages.exchange.upToAmt"),
  };
  const amt = (min: number | null, max: number | null, currency: string) =>
    fmtAmount(min, max, currency, amtLabels);

  const [tab, setTab] = useState<"mine" | "browse">("mine");
  // The match whose contract dialog is open, if any.
  const [contractFor, setContractFor] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [browse, setBrowse] = useState<any[]>([]);
  const [browseType, setBrowseType] = useState<ListingType | "">("");
  const [browseQ, setBrowseQ] = useState("");
  const [browseIndustry, setBrowseIndustry] = useState("");
  const [browseCountry, setBrowseCountry] = useState("");
  const [loading, setLoading] = useState(true);

  // Create flow
  const [draftType, setDraftType] = useState<ListingType | null>(null);
  const [creating, setCreating] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [attachment, setAttachment] = useState<File | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);

  async function loadMine() {
    setLoading(true);
    try {
      const r = await fetchMine();
      setData(r);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadMine();
  }, []);

  useEffect(() => {
    if (tab !== "browse") return;
    fetchBrowse({
      data: {
        type: browseType || undefined,
        q: browseQ || undefined,
        industry: browseIndustry || undefined,
        country: browseCountry || undefined,
      },
    })
      .then((r) => setBrowse(r.listings))
      .catch(() => setBrowse([]));
  }, [tab, browseType, browseQ, browseIndustry, browseCountry, fetchBrowse]);

  const matchesByListing = useMemo(() => {
    const map = new Map<string, any[]>();
    if (!data) return map;
    const mine = new Set(data.listings.map((l: any) => l.id));
    for (const m of data.matches) {
      if (m.status === "dismissed") continue;
      const myId = mine.has(m.listing_a) ? m.listing_a : m.listing_b;
      const otherId = myId === m.listing_a ? m.listing_b : m.listing_a;
      const other = data.counterparts[otherId];
      if (!mine.has(myId)) continue;
      const arr = map.get(myId) ?? [];
      arr.push({ ...m, other, inbound: myId === m.listing_b });
      map.set(myId, arr);
    }
    return map;
  }, [data]);

  function startDraft(t: ListingType) {
    setDraftType(t);
    setShowPicker(false);
    setForm({});
  }

  async function submitDraft(e: React.FormEvent) {
    e.preventDefault();
    if (!draftType) return;
    const meta = TYPE_META[draftType];
    setCreating(true);
    try {
      // Optional attachment (pitch deck, catalogue) — uploaded to the public
      // listing-files bucket before the listing is created.
      let attachmentUrl: string | undefined;
      let attachmentName: string | undefined;
      if (attachment) {
        if (attachment.size > 10 * 1024 * 1024)
          throw new Error(t("app.pages.exchange.attachTooBig"));
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error(t("app.pages.exchange.signInAgain"));
        const path = `${userData.user.id}/${crypto.randomUUID()}-${safeStorageFileName(attachment.name)}`;
        const { error: upErr } = await supabase.storage
          .from("listing-files")
          .upload(path, attachment, { contentType: attachment.type || "application/octet-stream" });
        if (upErr) throw upErr;
        attachmentUrl = supabase.storage.from("listing-files").getPublicUrl(path).data.publicUrl;
        attachmentName = attachment.name.slice(0, 160);
      }
      const details: Record<string, string> = {};
      for (const f of meta.fields) if (form[f.key]?.trim()) details[f.key] = form[f.key].trim();
      const r = await create({
        data: {
          type: draftType,
          attachmentUrl,
          attachmentName,
          title: form.title ?? "",
          description: form.description?.trim() || undefined,
          industry: form.industry?.trim() || undefined,
          country: form.country?.trim() || undefined,
          amountMin: form.amountMin ? Number(form.amountMin) : undefined,
          amountMax: form.amountMax ? Number(form.amountMax) : undefined,
          currency: (form.currency as any) || "USD",
          details,
        },
      });
      toast.success(
        r.matches > 0
          ? t("app.pages.exchange.liveMatches", { count: r.matches })
          : t("app.pages.exchange.liveNone"),
      );
      setDraftType(null);
      setAttachment(null);
      await loadMine();
    } catch (err: any) {
      toast.error(err?.message ?? t("app.pages.exchange.couldNotCreate"));
    } finally {
      setCreating(false);
    }
  }

  async function doRefresh(id: string) {
    setRefreshing(id);
    try {
      const r = await refreshMatches({ data: { id } });
      toast.success(t("app.pages.exchange.matchesFound", { count: r.matches }));
      await loadMine();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setRefreshing(null);
    }
  }

  async function changeListing(id: string, status: "active" | "paused" | "closed") {
    await setListingStatus({ data: { id, status } });
    await loadMine();
  }

  async function markMatch(id: string, status: "contacted" | "dismissed") {
    await setMatchStatus({ data: { id, status } });
    await loadMine();
  }

  const inputCls =
    "w-full rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";

  return (
    <DashboardShell
      title={t("app.pages.exchange.title")}
      subtitle={t("app.pages.exchange.subtitle")}
      actions={
        <button
          onClick={() => {
            setShowPicker(true);
            setDraftType(null);
          }}
          className="rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium flex items-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" /> {t("app.pages.exchange.newListing")}
        </button>
      }
    >
      <div className="mb-4 flex gap-2">
        {(
          [
            ["mine", t("app.pages.exchange.myListings")],
            ["browse", t("app.pages.exchange.browseNetwork")],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`text-xs px-3.5 py-1.5 rounded-full border ${
              tab === k
                ? "brand-gradient text-black border-transparent"
                : "glass border-border text-muted-foreground hover:bg-white/5"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Type picker */}
      {showPicker && (
        <div className="glass-strong rounded-2xl p-6 mb-4 relative">
          <button
            onClick={() => setShowPicker(false)}
            className="absolute top-4 right-4 text-muted-foreground hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="font-display text-lg font-semibold mb-1">
            {t("app.pages.exchange.pickerTitle")}
          </div>
          <p className="text-xs text-muted-foreground mb-5">{t("app.pages.exchange.pickerSub")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {LISTING_TYPES.map((lt) => {
              const meta = TYPE_META[lt];
              const Icon = meta.icon;
              return (
                <button
                  key={lt}
                  onClick={() => startDraft(lt)}
                  className="text-left rounded-2xl glass border border-border p-4 hover:bg-white/5 transition group"
                >
                  <Icon
                    className="h-5 w-5 mb-3"
                    style={{ color: `oklch(0.82 0.15 ${meta.hue})` }}
                  />
                  <div className="font-display font-semibold text-sm">{tmLabel(lt)}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {tmTagline(lt)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Structured form */}
      {draftType && (
        <form onSubmit={submitDraft} className="glass-strong rounded-2xl p-6 mb-4 relative">
          <button
            type="button"
            onClick={() => setDraftType(null)}
            className="absolute top-4 right-4 text-muted-foreground hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-5">
            <TypeBadge type={draftType} />
            <span className="text-xs text-muted-foreground">{tmTagline(draftType)}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">
                {t("app.pages.exchange.titleLabel")}
              </label>
              <input
                required
                minLength={4}
                maxLength={120}
                value={form.title ?? ""}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t("app.pages.exchange.titlePh")}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">
                {t("app.pages.exchange.descLabel")}
              </label>
              <textarea
                rows={3}
                maxLength={2000}
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("app.pages.exchange.descPh")}
                className={`${inputCls} resize-none`}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("app.pages.exchange.industryLabel")}
              </label>
              <input
                value={form.industry ?? ""}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                placeholder={t("app.pages.exchange.industryPh")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("app.pages.exchange.countryLabel")}
              </label>
              <input
                value={form.country ?? ""}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder={t("app.pages.exchange.countryPh")}
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:col-span-2">
              <div>
                <label className="text-xs text-muted-foreground">
                  {tmAmount(draftType)} {t("app.pages.exchange.min")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.amountMin ?? ""}
                  onChange={(e) => setForm({ ...form, amountMin: e.target.value })}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  {t("app.pages.exchange.max")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.amountMax ?? ""}
                  onChange={(e) => setForm({ ...form, amountMax: e.target.value })}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  {t("app.pages.exchange.currencyLabel")}
                </label>
                <select
                  value={form.currency ?? "USD"}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className={inputCls}
                >
                  {["USD", "EUR", "CHF", "GBP"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">
                {t("app.pages.exchange.attachLabel")}
              </label>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer rounded-xl glass border border-border px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5" />
                  {attachment ? attachment.name : t("app.pages.exchange.chooseFile")}
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
                  />
                </label>
                {attachment && (
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    className="text-muted-foreground hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {TYPE_META[draftType].fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs text-muted-foreground">
                  {t(`app.pages.exchange.types.${draftType}.f.${f.key}`, {
                    defaultValue: f.label,
                  })}
                </label>
                <input
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={t(`app.pages.exchange.types.${draftType}.p.${f.key}`, {
                    defaultValue: f.placeholder,
                  })}
                  maxLength={300}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
          <button
            type="submit"
            disabled={creating || !(form.title ?? "").trim()}
            className="mt-5 rounded-full brand-gradient text-black px-6 py-2.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            {creating ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />{" "}
                {t("app.pages.exchange.publishing")}
              </>
            ) : (
              <>
                <ArrowLeftRight className="h-3.5 w-3.5" /> {t("app.pages.exchange.publishFind")}
              </>
            )}
          </button>
        </form>
      )}

      {tab === "mine" ? (
        loading ? (
          <ListSkeleton rows={4} />
        ) : !data || data.listings.length === 0 ? (
          <div className="glass-strong rounded-2xl p-12 text-center">
            <ArrowLeftRight className="h-8 w-8 mx-auto text-[oklch(0.82_0.18_168)] mb-3" />
            <div className="font-display text-lg font-semibold">
              {t("app.pages.exchange.emptyTitle")}
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              {t("app.pages.exchange.emptyBody")}
            </p>
            <button
              onClick={() => setShowPicker(true)}
              className="mt-5 rounded-full brand-gradient text-black px-5 py-2.5 text-sm font-medium inline-flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> {t("app.pages.exchange.createFirst")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {data.listings.map((l: any) => {
              const ms = matchesByListing.get(l.id) ?? [];
              return (
                <div key={l.id} className="glass-strong rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <TypeBadge type={l.type} />
                        {l.status !== "active" && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-border text-muted-foreground uppercase tracking-wide">
                            {t(
                              `app.pages.exchange.st${l.status.charAt(0).toUpperCase()}${l.status.slice(1)}`,
                              { defaultValue: l.status },
                            )}
                          </span>
                        )}
                      </div>
                      <div className="font-display text-lg mt-2">{l.title}</div>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {amt(l.amount_min, l.amount_max, l.currency) && (
                          <span className="inline-flex items-center gap-1">
                            <Banknote className="h-3 w-3" />{" "}
                            {amt(l.amount_min, l.amount_max, l.currency)}
                          </span>
                        )}
                        {l.industry && <span>{l.industry}</span>}
                        {l.country && (
                          <span className="inline-flex items-center gap-1">
                            <Globe2 className="h-3 w-3" /> {l.country}
                          </span>
                        )}
                      </div>
                      {l.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {l.description}
                        </p>
                      )}
                      {l.attachment_url && (
                        <a
                          href={l.attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-[oklch(0.82_0.18_168)] hover:underline"
                        >
                          <Paperclip className="h-3 w-3" /> {l.attachment_name ?? "Attachment"}
                        </a>
                      )}
                      <DetailChips details={l.details} />
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => doRefresh(l.id)}
                        disabled={refreshing === l.id || l.status !== "active"}
                        title={t("app.pages.exchange.matchBtn")}
                        className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-1 disabled:opacity-40"
                      >
                        <RefreshCw
                          className={`h-3 w-3 ${refreshing === l.id ? "animate-spin" : ""}`}
                        />{" "}
                        {t("app.pages.exchange.matchBtn")}
                      </button>
                      {l.status === "active" ? (
                        <button
                          onClick={() => changeListing(l.id, "paused")}
                          className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                        >
                          {t("app.pages.exchange.pause")}
                        </button>
                      ) : (
                        <button
                          onClick={() => changeListing(l.id, "active")}
                          className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                        >
                          {t("app.pages.exchange.activate")}
                        </button>
                      )}
                      <button
                        onClick={() => changeListing(l.id, "closed")}
                        className="rounded-full glass border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5"
                      >
                        {t("app.pages.exchange.close")}
                      </button>
                    </div>
                  </div>

                  {ms.length > 0 && (
                    <div className="mt-4 border-t border-border pt-4 space-y-3">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                        {t("app.pages.exchange.matchesCount", { count: ms.length })}
                      </div>
                      {ms.map((m: any) => (
                        <div
                          key={m.id}
                          className="rounded-xl glass border border-border p-4 flex flex-wrap gap-4 items-start"
                        >
                          <div
                            className="h-11 w-11 rounded-xl flex items-center justify-center font-display font-bold text-lg shrink-0"
                            style={{
                              background: `oklch(0.82 0.15 ${TYPE_META[(m.other?.type ?? "product_offer") as ListingType]?.hue ?? "168"} / 0.15)`,
                              color: `oklch(0.82 0.15 ${TYPE_META[(m.other?.type ?? "product_offer") as ListingType]?.hue ?? "168"})`,
                            }}
                          >
                            {m.score}
                          </div>
                          <div className="flex-1 min-w-52">
                            <div className="text-sm font-medium">
                              {m.other?.title ?? "Listing"}{" "}
                              <span className="text-muted-foreground font-normal">
                                · {m.other?.company_name ?? "Company"}
                              </span>
                              {m.inbound && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[oklch(0.82_0.18_168_/_0.12)] text-[oklch(0.82_0.18_168)]">
                                  {t("app.pages.exchange.inbound")}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{m.reason}</p>
                            {m.other?.attachment_url && (
                              <a
                                href={m.other.attachment_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 inline-flex items-center gap-1.5 text-xs text-[oklch(0.82_0.18_168)] hover:underline"
                              >
                                <Paperclip className="h-3 w-3" />{" "}
                                {m.other.attachment_name ?? "Attachment"}
                              </a>
                            )}
                            {m.other && <DetailChips details={m.other.details} />}
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            {m.other?.company_id && (
                              <Link
                                to="/marketplace/$id"
                                params={{ id: m.other.company_id }}
                                className="rounded-full brand-gradient text-black px-3 py-1.5 text-xs font-medium"
                              >
                                {t("app.pages.exchange.talkTwin")}
                              </Link>
                            )}
                            {m.status !== "contacted" && (
                              <button
                                onClick={() => markMatch(m.id, "contacted")}
                                className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                              >
                                {t("app.pages.exchange.contacted")}
                              </button>
                            )}
                            {/* A match used to stop at "contacted". This carries
                                it into a contract with the listing prefilled. */}
                            <button
                              onClick={() => setContractFor(m.id)}
                              className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 inline-flex items-center gap-1"
                            >
                              <FileSignature className="h-3 w-3" />
                              {t("app.pages.exchange.toContract", {
                                defaultValue: "Draft a contract",
                              })}
                            </button>
                            <button
                              onClick={() => markMatch(m.id, "dismissed")}
                              className="rounded-full glass border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-white/5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div>
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setBrowseType("")}
              className={`text-xs px-3 py-1.5 rounded-full border ${browseType === "" ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
            >
              {t("app.pages.exchange.all")}
            </button>
            {LISTING_TYPES.map((lt) => (
              <button
                key={lt}
                onClick={() => setBrowseType(lt)}
                className={`text-xs px-3 py-1.5 rounded-full border ${browseType === lt ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
              >
                {tmLabel(lt)}
              </button>
            ))}
            <div className="ml-auto flex flex-wrap gap-2">
              <input
                value={browseIndustry}
                onChange={(e) => setBrowseIndustry(e.target.value)}
                placeholder={t("app.pages.exchange.industryFilter")}
                className="w-32 rounded-full bg-white/5 border border-border px-4 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                value={browseCountry}
                onChange={(e) => setBrowseCountry(e.target.value)}
                placeholder={t("app.pages.exchange.countryFilter")}
                className="w-28 rounded-full bg-white/5 border border-border px-4 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                value={browseQ}
                onChange={(e) => setBrowseQ(e.target.value)}
                placeholder={t("app.pages.exchange.searchListings")}
                className="w-40 rounded-full bg-white/5 border border-border px-4 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
            </div>
          </div>
          {browse.length === 0 ? (
            <div className="glass-strong rounded-2xl p-10 text-center text-sm text-muted-foreground">
              {t("app.pages.exchange.noBrowse")}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {browse.map((l: any) => (
                <div key={l.id} className="glass-strong rounded-2xl p-5 flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <TypeBadge type={l.type} />
                    <span className="text-xs text-muted-foreground">{l.company_name}</span>
                  </div>
                  <div className="font-display text-lg mt-2">{l.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {amt(l.amount_min, l.amount_max, l.currency) && (
                      <span className="inline-flex items-center gap-1">
                        <Banknote className="h-3 w-3" />{" "}
                        {amt(l.amount_min, l.amount_max, l.currency)}
                      </span>
                    )}
                    {l.industry && <span>{l.industry}</span>}
                    {l.country && <span>{l.country}</span>}
                  </div>
                  {l.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-3 flex-1">
                      {l.description}
                    </p>
                  )}
                  <DetailChips details={l.details} />
                  <Link
                    to="/marketplace/$id"
                    params={{ id: l.company_id }}
                    className="mt-4 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium text-center"
                  >
                    {t("app.pages.exchange.talkCompanyTwin")}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {contractFor && (
        <ContractFromMatch matchId={contractFor} onClose={() => setContractFor(null)} />
      )}
    </DashboardShell>
  );
}
